import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import github from "../../context/github/githubApi";

/** Route path — not linked in the app UI; share only with people who know the code. */
export const RATE_LIMITS_ROUTE = "/system/rate-limits";

const SESSION_KEY = "gf-rate-limits-unlocked";

/** Set REACT_APP_RATE_LIMITS_CODE in .env / Vercel to change; default for local use. */
function getExpectedCode() {
  return String(
    process.env.REACT_APP_RATE_LIMITS_CODE || "112233"
  ).trim();
}

const PRIORITY_KEYS = ["core", "search", "graphql", "code_search"];

function formatReset(resetSec) {
  if (resetSec == null || Number.isNaN(Number(resetSec))) return "—";
  const d = new Date(Number(resetSec) * 1000);
  return d.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function pctUsed(used, limit) {
  if (!limit || limit <= 0) return 0;
  return Math.min(100, Math.round((used / limit) * 100));
}

function ResourceMeter({ title, data, subtitle }) {
  if (!data) return null;
  const { limit = 0, used = 0, remaining, reset } = data;
  const pct = pctUsed(used, limit);
  const warn = pct >= 85;
  const bad = pct >= 95 || remaining === 0;

  return (
    <div className="gf-rate-card gf-card">
      <div className="gf-rate-card__head">
        <h2 className="gf-rate-card__title">{title}</h2>
        {subtitle ? <p className="gf-muted gf-rate-card__sub">{subtitle}</p> : null}
      </div>
      <div className="gf-rate-card__stats">
        <span>
          <strong>{remaining ?? "—"}</strong> <span className="gf-muted">/ {limit}</span>{" "}
          <span className="gf-muted">remaining</span>
        </span>
        <span className="gf-muted">
          Used: <strong>{used}</strong>
        </span>
      </div>
      <div
        className={`gf-rate-card__bar${bad ? " is-danger" : warn ? " is-warn" : ""}`}
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div className="gf-rate-card__bar-fill" style={{ width: `${pct}%` }} />
      </div>
      <p className="gf-rate-card__reset gf-muted">
        Resets around:{" "}
        <time dateTime={reset ? new Date(Number(reset) * 1000).toISOString() : undefined}>
          {formatReset(reset)}
        </time>
      </p>
    </div>
  );
}

const RateLimitsPage = () => {
  const [unlocked, setUnlocked] = useState(() => {
    try {
      return sessionStorage.getItem(SESSION_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [codeInput, setCodeInput] = useState("");
  const [codeErr, setCodeErr] = useState(null);

  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setErr(null);
    setLoading(true);
    try {
      const res = await github.get("/rate_limit", {
        validateStatus: (s) => s >= 200 && s < 600,
      });
      if (res.status !== 200) {
        setData(null);
        setErr(
          res.data?.message ||
            `Request failed (${res.status}). Add a GitHub token for higher limits.`
        );
        return;
      }
      setData(res.data);
    } catch (e) {
      setData(null);
      setErr(e?.message || "Could not load rate limit data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let meta = document.querySelector('meta[name="robots"]');
    const created = !meta;
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "robots");
      document.head.appendChild(meta);
    }
    const prev = meta.getAttribute("content");
    meta.setAttribute("content", "noindex, nofollow");
    return () => {
      if (created) {
        meta.remove();
      } else if (prev != null) {
        meta.setAttribute("content", prev);
      } else {
        meta.removeAttribute("content");
      }
    };
  }, []);

  useEffect(() => {
    document.title = unlocked
      ? "GitHub API usage · GitHub Finder"
      : "Access required · GitHub Finder";
    return () => {
      document.title = "GitHub Finder | Search GitHub Developers & Repositories";
    };
  }, [unlocked]);

  useEffect(() => {
    if (!unlocked) return;
    load();
  }, [unlocked, load]);

  const onSubmitCode = (e) => {
    e.preventDefault();
    setCodeErr(null);
    const expected = getExpectedCode();
    if (!expected) {
      setCodeErr("Access code is not configured on the server.");
      return;
    }
    if (codeInput.trim() === expected) {
      try {
        sessionStorage.setItem(SESSION_KEY, "1");
      } catch {
        /* ignore */
      }
      setUnlocked(true);
      setCodeInput("");
    } else {
      setCodeErr("Incorrect code.");
    }
  };

  const resources = data?.resources || {};
  const topRate = data?.rate;

  const otherEntries = Object.entries(resources).filter(
    ([k]) => !PRIORITY_KEYS.includes(k)
  );

  if (!unlocked) {
    return (
      <main className="gf-container gf-page gf-rate-limits-page">
        <nav className="gf-rate-limits__crumb gf-muted" aria-label="Breadcrumb">
          <Link to="/">Home</Link>
        </nav>
        <div className="gf-rate-limits__gate gf-card">
          <h1 className="gf-rate-limits__gate-title">Restricted</h1>
          <p className="gf-muted gf-rate-limits__gate-text">
            Enter the access code to continue.
          </p>
          <form className="gf-rate-limits__gate-form" onSubmit={onSubmitCode} autoComplete="off">
            <label className="gf-rate-limits__gate-label" htmlFor="gf-rate-code">
              Code
            </label>
            <input
              id="gf-rate-code"
              name="gf-rate-code"
              type="password"
              inputMode="numeric"
              className="gf-rate-limits__gate-input"
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value)}
              placeholder="••••••"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
            />
            {codeErr ? (
              <p className="gf-rate-limits__gate-err" role="alert">
                {codeErr}
              </p>
            ) : null}
            <button type="submit" className="gf-btn gf-btn--primary gf-rate-limits__gate-btn">
              Continue
            </button>
          </form>
        </div>
      </main>
    );
  }

  return (
    <main className="gf-container gf-page gf-rate-limits-page">
      <nav className="gf-rate-limits__crumb gf-muted" aria-label="Breadcrumb">
        <Link to="/">Home</Link>
        <span aria-hidden="true"> / </span>
        <span>API usage</span>
      </nav>

      <header className="gf-rate-limits__hero">
        <h1 className="gf-rate-limits__title">GitHub API usage</h1>
        <p className="gf-rate-limits__lead gf-muted">
          Live limits for this app’s token (via your Vercel proxy or local token). Search has a
          separate, smaller pool than REST &quot;core&quot;.
        </p>
        <button
          type="button"
          className="gf-btn gf-btn--secondary gf-rate-limits__refresh"
          onClick={load}
          disabled={loading}
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </header>

      {err ? (
        <div className="gf-rate-limits__alert gf-card" role="alert">
          {err}
        </div>
      ) : null}

      {loading && !data ? (
        <p className="gf-muted">Loading rate limits…</p>
      ) : null}

      {data ? (
        <>
          <section className="gf-rate-limits__grid" aria-label="Primary limits">
            <ResourceMeter
              title="REST (core)"
              subtitle="Most user/repo API calls use this pool."
              data={resources.core}
            />
            <ResourceMeter
              title="Search"
              subtitle="User & repository search — stricter per-minute cap."
              data={resources.search}
            />
            <ResourceMeter
              title="GraphQL"
              subtitle="If you use GraphQL elsewhere."
              data={resources.graphql}
            />
          </section>

          {resources.code_search ? (
            <section className="gf-rate-limits__secondary" aria-label="Code search">
              <ResourceMeter title="Code search" data={resources.code_search} />
            </section>
          ) : null}

          {topRate && (
            <p className="gf-rate-limits__note gf-muted">
              Top-level <code className="gf-code">rate</code> mirrors{" "}
              <code className="gf-code">core</code> for this token (used {topRate.used} / {topRate.limit}
              ).
            </p>
          )}

          {otherEntries.length > 0 ? (
            <section className="gf-rate-limits__table-wrap gf-card" aria-label="Other resources">
              <h2 className="gf-rate-limits__table-title">Other resource pools</h2>
              <div className="gf-rate-limits__table-scroll">
                <table className="gf-rate-limits__table">
                  <thead>
                    <tr>
                      <th>Resource</th>
                      <th>Limit</th>
                      <th>Used</th>
                      <th>Remaining</th>
                      <th>Reset</th>
                    </tr>
                  </thead>
                  <tbody>
                    {otherEntries.map(([name, v]) => (
                      <tr key={name}>
                        <td>
                          <code className="gf-code">{name}</code>
                        </td>
                        <td>{v?.limit ?? "—"}</td>
                        <td>{v?.used ?? "—"}</td>
                        <td>{v?.remaining ?? "—"}</td>
                        <td>{formatReset(v?.reset)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}

          <details className="gf-rate-limits__raw gf-card">
            <summary>Raw JSON</summary>
            <pre className="gf-rate-limits__pre">{JSON.stringify(data, null, 2)}</pre>
          </details>
        </>
      ) : null}

      <section className="gf-rate-limits__traffic gf-card" aria-label="Site traffic">
        <h2 className="gf-rate-limits__traffic-title">Site visitors &amp; traffic</h2>
        <p>
          Live visitor counts are <strong>not</strong> stored in this app’s database. On{" "}
          <strong>Vercel</strong>, enable{" "}
          <a href="https://vercel.com/docs/analytics" rel="noopener noreferrer" target="_blank">
            Web Analytics
          </a>{" "}
          in your project — then open <strong>Vercel → your project → Analytics</strong> for
          visitors, top pages, and countries.
        </p>
        <p className="gf-muted gf-rate-limits__traffic-note">
          This page only shows <strong>GitHub API</strong> usage for debugging token limits.
        </p>
      </section>
    </main>
  );
};

export default RateLimitsPage;
